import { ProgrammeWorkout } from 'src/dao/core-dao'
import { deriveProgrammeWorkoutProgress } from 'src/hook/programme-workout-progress-derive'
import { useProgrammeWorkoutProgressData } from 'src/hook/use-programme-workout-progress-data'
import { useMemo } from 'react'

export type UseProgrammeWorkoutProgressResult = {
  completedClassesCount: number | undefined
  totalClassesCount: number | undefined
  isProgressReady: boolean
  nextIncompleteClassTitle: string | undefined
  nextIncompleteProgrammeWorkout: ProgrammeWorkout | undefined
  nextIncompleteWorkoutId: string | undefined
  nextIncompleteWeek: number | undefined
}

/**
 * Single hook that returns both completion counts and next-incomplete workout data.
 * Runs the derivation once instead of twice when both pieces of data are needed.
 */
export const useProgrammeWorkoutProgress = (programmeId: string): UseProgrammeWorkoutProgressResult => {
  const { programmeWorkouts, completedProgrammeWorkouts, isQueriesFetched } = useProgrammeWorkoutProgressData(programmeId)

  return useMemo((): UseProgrammeWorkoutProgressResult => {
    if (!programmeId || !isQueriesFetched) {
      return {
        completedClassesCount: undefined,
        totalClassesCount: undefined,
        isProgressReady: false,
        nextIncompleteClassTitle: undefined,
        nextIncompleteProgrammeWorkout: undefined,
        nextIncompleteWorkoutId: undefined,
        nextIncompleteWeek: undefined,
      }
    }
    const derived = deriveProgrammeWorkoutProgress(programmeWorkouts, completedProgrammeWorkouts)
    return {
      completedClassesCount: derived.completedClassesCount,
      totalClassesCount: derived.totalClassesCount,
      isProgressReady: true,
      nextIncompleteClassTitle: derived.nextIncompleteClassTitle,
      nextIncompleteProgrammeWorkout: derived.nextIncompleteProgrammeWorkout,
      nextIncompleteWorkoutId: derived.nextIncompleteWorkoutId,
      nextIncompleteWeek: derived.nextIncompleteWeek,
    }
  }, [programmeId, programmeWorkouts, completedProgrammeWorkouts, isQueriesFetched])
}
